import { gql, useMutation, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import { Code } from 'src/components/code/Code';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';
import {
  GRAPH_VARIANT_PLACEHOLDER,
  introspectionToSubgraphCheck,
  introspectionToSubgraphPublish,
  schemaFileToSubgraphCheck,
  schemaFileToSubgraphPublish,
} from 'src/lib/roverCommands';

export const RoverAddSubgraphModal = ({
  isOpen,
  onClose,
  graphRef,
  title,
  description,
  withNewVariant,
  finalFocusRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphRef: GraphRef;
  title?: React.ReactNode;
  description?: React.ReactNode;
  withNewVariant: boolean;
  finalFocusRef?: React.RefObject<HTMLButtonElement>;
}) => {
  const [schemaCheckSchemaSource, setSchemaCheckSchemaSource] = useState<
    'schemaFile' | 'introspection'
  >('schemaFile');
  const [schemaPublishSchemaSource, setSchemaPublishSchemaSource] = useState<
    'schemaFile' | 'introspection'
  >('schemaFile');

  const { data: userData } = useQuery<
    GraphQLTypes.AddSubgraphModalQuery,
    GraphQLTypes.AddSubgraphModalQueryVariables
  >(gql`
    query AddSubgraphModalQuery {
      me {
        id
      }
    }
  `);

  const [provisionKey, { data, error }] = useMutation<
    GraphQLTypes.AddSubgraphModalProvisionKeyMutation,
    GraphQLTypes.AddSubgraphModalProvisionKeyMutationVariables
  >(gql`
    mutation AddSubgraphModalProvisionKeyMutation($userId: ID!) {
      user(id: $userId) {
        provisionKey {
          apiKey {
            id
            token
          }
        }
      }
    }
  `);

  React.useEffect(() => {
    if (userData?.me?.id) {
      provisionKey({
        variables: {
          userId: userData.me.id,
        },
      });
    }
  }, [provisionKey, userData]);

  const key = data?.user?.provisionKey?.apiKey.token;

  const { graphId, graphVariant } = graphRef;

  const subgraphCheck =
    schemaCheckSchemaSource === 'schemaFile'
      ? schemaFileToSubgraphCheck({
          graphId,
          graphVariant: withNewVariant
            ? GRAPH_VARIANT_PLACEHOLDER
            : graphVariant,
          highlightGraphRef: withNewVariant,
        })
      : introspectionToSubgraphCheck({
          graphId,
          graphVariant: withNewVariant
            ? GRAPH_VARIANT_PLACEHOLDER
            : graphVariant,
          highlightGraphRef: withNewVariant,
        });

  const subgraphPublish =
    schemaPublishSchemaSource === 'schemaFile'
      ? schemaFileToSubgraphPublish({
          graphId,
          graphVariant: withNewVariant
            ? GRAPH_VARIANT_PLACEHOLDER
            : graphVariant,
          highlightGraphRef: withNewVariant,
        })
      : introspectionToSubgraphPublish({
          graphId,
          graphVariant: withNewVariant
            ? GRAPH_VARIANT_PLACEHOLDER
            : graphVariant,
          highlightGraphRef: withNewVariant,
        });

  const initialRef = React.useRef(null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      finalFocusRef={finalFocusRef}
      initialFocusRef={initialRef}
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {title || <>Adding a subgraph to {graphRefToString(graphRef)}</>}
          </ModalTitle>
          <ModalDescription>
            {description || (
              <>
                To add another subgraph to this variant, follow these steps
                after you have deployed the subgraph to the cloud provider of
                your choice:
              </>
            )}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <p className="text-base">
            1. Let’s start by installing Rover
            <br />
            <span className="italic text-secondary">
              If you already have Rover installed, skip to the next step!
            </span>
          </p>

          <div className="flex flex-col gap-2">
            <Code
              title="Linux / MacOS installer"
              firstLineDecorator="$ "
              code="curl -sSL https://rover.apollo.dev/nix/latest | sh"
              showCopyButton
            />
            <Code
              title="Windows PowerShell"
              firstLineDecorator="> "
              code="iwr 'https://rover.apollo.dev/win/latest' | iex"
              showCopyButton
            />
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <p className="text-base">2. Connect your CLI to Studio:</p>
            <Code
              firstLineDecorator="$ "
              code="rover config auth"
              showCopyButton
            />
            <div className="flex items-center">
              <IconInfo className="mr-2 text-icon-secondary" />
              <Link
                isExternal
                to={DocsRouter.path('RoverCiCdSetup')}
                className="text-sm"
              >
                Learn how to use Rover to setup your CI/CD.
              </Link>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <p className="text-base">
              3. Copy this user token we’ve automatically generated for you and
              provide it to Rover.
            </p>
            <Code
              code={key ?? '[Token Unavailable]'}
              mask={error || !key ? undefined : key.split(':').pop()}
              showCopyButton
            />
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <p className="text-base">
              4. Once you’ve provided your user token, run a check to make sure
              there are no breaking changes.
            </p>
            <div className="flex gap-4 border-b border-primary">
              <HeaderTab
                className="focus-visible:focus-ring mx-0"
                tabID="schemaFile"
                tabName="Schema File"
                isActive={() => schemaCheckSchemaSource === 'schemaFile'}
                onClick={() => {
                  setSchemaCheckSchemaSource('schemaFile');
                }}
              />
              <HeaderTab
                className="focus-visible:focus-ring mx-0"
                tabID="introspection"
                tabName="Introspection"
                isActive={() => schemaCheckSchemaSource === 'introspection'}
                onClick={() => {
                  setSchemaCheckSchemaSource('introspection');
                }}
              />
            </div>
            <Code
              firstLineDecorator="$ "
              code={subgraphCheck.shellString}
              showCopyButton
              highlight={subgraphCheck.shellStringLinesToHighlight}
            />
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <p className="text-base">
              5. Finally, re-deploy your changes and publish your subgraph by
              running the following command:
            </p>
            <div className="flex gap-4 border-b border-primary">
              <HeaderTab
                className="focus-visible:focus-ring mx-0"
                tabID="schemaFile"
                tabName="Schema File"
                isActive={() => schemaPublishSchemaSource === 'schemaFile'}
                onClick={() => {
                  setSchemaPublishSchemaSource('schemaFile');
                }}
              />
              <HeaderTab
                className="focus-visible:focus-ring mx-0"
                tabID="introspection"
                tabName="Introspection"
                isActive={() => schemaPublishSchemaSource === 'introspection'}
                onClick={() => {
                  setSchemaPublishSchemaSource('introspection');
                }}
              />
            </div>
            <Code
              firstLineDecorator="$ "
              code={subgraphPublish.shellString}
              showCopyButton
              highlight={subgraphPublish.shellStringLinesToHighlight}
            />
          </div>
        </ModalBody>
        <ModalFooter className="gap-4">
          <div>
            We recommend you secure the connection to your subgraphs. To learn
            how,{' '}
            <Link isExternal to={DocsRouter.path('SecureSubgraphs')}>
              head over to our docs.
            </Link>
          </div>
          <Button variant="secondary" onClick={onClose} ref={initialRef}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
